import { Button, Card, ErrorBox, Link, Loading, Page } from '@elsa/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { fmtDateTime, fmtDay } from '../lib/format.ts';
import { useOrg } from '../lib/session.tsx';
import { app, must, seap } from '../lib/supabase.ts';
import type { Notification } from '../lib/types.ts';
import { markRead } from './AppLayout.tsx';

function AlertItems({ ids }: { ids: number[] }) {
  const q = useQuery({
    queryKey: ['acq_names', ids],
    queryFn: () =>
      must(
        seap()
          .from('acquisitions')
          .select('id, name, finalization_day, closing_value_ron, contracting_authority_text')
          .in('id', ids.slice(0, 50)),
      ) as Promise<
        {
          id: number;
          name: string | null;
          finalization_day: string;
          contracting_authority_text: string | null;
        }[]
      >,
  });
  if (q.isLoading) return <Loading />;
  const byId = new Map((q.data ?? []).map((a) => [a.id, a]));
  return (
    <ul className="mt-2 space-y-0.5 text-sm">
      {ids.map((id) => {
        const a = byId.get(id);
        return (
          <li key={id}>
            <Link to={`/app/achizitii/${id}`} className="hover:underline">
              {a?.name ?? `Achiziția ${id}`}
            </Link>
            {a ? (
              <span className="text-xs text-gri-700"> · {fmtDay(a.finalization_day)}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function NotificationsPage() {
  const { session } = useOrg();
  const qc = useQueryClient();
  const [open, setOpen] = useState<number | null>(null);
  const q = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () =>
      must(
        app()
          .from('notifications')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .limit(100),
      ) as Promise<Notification[]>,
  });
  const read = useMutation({
    mutationFn: (ids: number[]) => markRead(ids),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const unread = (q.data ?? []).filter((n) => !n.read_at).map((n) => n.id);
  return (
    <Page
      title="Notificări"
      actions={
        unread.length ? (
          <Button onClick={() => read.mutate(unread)} disabled={read.isPending}>
            Marchează toate ca citite
          </Button>
        ) : null
      }
    >
      <ErrorBox error={q.error ?? read.error} />
      {q.isLoading ? <Loading /> : null}
      {q.data && !q.data.length ? (
        <p className="text-sm text-gri-700">
          Nicio notificare încă. Setează o{' '}
          <Link to="/app/alerte" className="underline">
            alertă
          </Link>
          .
        </p>
      ) : null}
      <div className="space-y-2">
        {q.data?.map((n) => (
          <Card key={n.id} className={n.read_at ? 'opacity-75' : ''}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="font-medium">
                  {!n.read_at ? <span className="mr-1.5 inline-block h-2 w-2 bg-semnal" /> : null}
                  {n.title}
                </div>
                <div className="text-xs text-gri-700">{fmtDateTime(n.created_at)}</div>
                {n.body ? <p className="mt-1 text-sm">{n.body}</p> : null}
              </div>
              <div className="flex gap-2">
                {n.data?.acquisition_ids?.length ? (
                  <Button
                    onClick={() => {
                      setOpen(open === n.id ? null : n.id);
                      if (!n.read_at) read.mutate([n.id]);
                    }}
                  >
                    {open === n.id ? 'Ascunde' : 'Vezi achizițiile'}
                  </Button>
                ) : null}
                {!n.read_at ? (
                  <Button tone="ghost" onClick={() => read.mutate([n.id])}>
                    Citită
                  </Button>
                ) : null}
              </div>
            </div>
            {open === n.id && n.data?.acquisition_ids ? (
              <AlertItems ids={n.data.acquisition_ids} />
            ) : null}
          </Card>
        ))}
      </div>
    </Page>
  );
}
